import {BehaviorSubject, forkJoin, Observable, of, Subject} from 'rxjs';
import {PrescriptionLine} from "@core/models/PrescriptionLine";
import {Injectable} from "@angular/core";
import {environment} from "@enviroment/environment";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {DefaultResponse} from "@core/models/Http/DefaultResponse";
import {GeneralFunctionsService} from "@core/service/generalFunctions.service";
import {catchError, map} from "rxjs/operators";
import {HttpMesageHandlerService} from "@core/service/http-mesage-handler";
import {decryptData} from "@shared/utils/decrypt";

@Injectable({
  providedIn: 'root',
})
export class PrescriptionLineService {
  htmlResponseSubject = new Subject<string>();
  private prescriptionLines: PrescriptionLine[] = [];
  dataChange: BehaviorSubject<PrescriptionLine[]> = new BehaviorSubject<PrescriptionLine[]>(this.prescriptionLines);

  constructor(
    private http: HttpClient,
    private gfs: GeneralFunctionsService,
    private httpMsgHandler: HttpMesageHandlerService
  ) {
  }

  get data(): PrescriptionLine[] {
    return this.dataChange.value;
  }

  /** CRUD methods */
  addPrescriptionLine(prescriptionLine: PrescriptionLine): number {
    const cleanData = this.gfs.mayusAcentos(prescriptionLine);

    this.prescriptionLines.push(new PrescriptionLine(cleanData));
    this.dataChange.next(this.prescriptionLines);
    return this.prescriptionLines.length - 1;
  }

  async updatePrescriptionLine(index: number, updatedLine: PrescriptionLine) {
    const cleanData = this.gfs.mayusAcentos(updatedLine);

    this.prescriptionLines[index] = new PrescriptionLine(cleanData);
    this.dataChange.next(this.prescriptionLines);
  }

  deletePrescriptionLine(index: number): void {
    this.prescriptionLines.splice(index, 1);
    this.dataChange.next(this.prescriptionLines);
  }

  getRelations(
    drug: string | number,
    idType: string
  ): Observable<{ routes: any[]; indications: any[]; units: any[] }> {

    const url = `${environment.backend_url}Consulta/GetRelaciones`;

    // Si es PARAFARMACIA (o id=0), NO usamos INDICATIONS aunque vengan
    const isParafarmacia = idType === 'PARAFARMACIA' || Number(drug) === 0;

    const id =
      typeof drug === 'number'
        ? drug
        : Number.isNaN(Number(drug))
          ? drug
          : Number(drug);

    // Aunque el backend ya ignore relacionType, lo mando por compatibilidad
    const body = { id, idType, relacionType: 'ROUTES' };

    return this.http.post<any>(url, body).pipe(
      map((res) => {
        // ✅ TU BACK devuelve: { code, message, data: { routes, indications, units }, ... }
        const payload = res?.data ?? {};

        const routes = Array.isArray(payload?.routes) ? payload.routes : [];
        const units = Array.isArray(payload?.units) ? payload.units : [];
        const indications = isParafarmacia
          ? []
          : (Array.isArray(payload?.indications) ? payload.indications : []);

        return { routes, indications, units };
      }),
      catchError(() => of({ routes: [], indications: [], units: [] }))
    );
  }


  postToAnalytics(data: any): void {
    const cleanData = this.gfs.mayusAcentos(data);
    const url = `${environment.backend_url}Consulta/Analisis`;

    this.http.post(url, cleanData).subscribe({
      next: (response: any) => {
        const html = response.data.htmlResponse;
        // @ts-ignore
        this.htmlResponseSubject.next(null);
        this.htmlResponseSubject.next(html);
      },
      error: (error: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(error);
      }
    });
  }

  postToAnalyticsXML(data: any) {
    const cleanData = this.gfs.mayusAcentos(data);

    const url = `${environment.backend_url}Consulta/AnalisisXml`;
    return this.http.post(url, cleanData);
  }

  postToPrescription(data: any) {
    const cleanData = this.gfs.mayusAcentos(data);

    const url = `${environment.backend_url}Recetas/RegistrarReceta`;
    return this.http.post<DefaultResponse<string>>(url, cleanData)
  }

  updatePrescription(data: any) {
    const cleanData = this.gfs.mayusAcentos(data);

    const url = `${environment.backend_url}Recetas/ActualizarReceta`;
    return this.http.put<DefaultResponse<string>>(url, cleanData)
  }

  getHtmlResponse() {
    return this.htmlResponseSubject.asObservable();
  }

  resetPrescriptionDataSource(): void {
    this.prescriptionLines = [];
    this.dataChange.next([]);
  }

  isLineValid(): boolean {
    const lastLine = this.prescriptionLines[this.prescriptionLines.length - 1];
    if (lastLine) {
      return Boolean(lastLine.dose)
        && Boolean(lastLine.duration)
        && Boolean(lastLine.durationType)
        && Boolean(lastLine.route)
        && Boolean(lastLine.frecuency)
    }
    return true;
  }

  postToPriscaInteractive(data: any) {
    const cleanData = this.gfs.mayusAcentos(data);
    const url = `${environment.backend_url}Consulta/AnalisisPriscaInteractive`;
    return this.http.post<any>(url, cleanData);
  }
}
